import { Accordion, AccordionTab } from 'primereact/accordion';
import { Checkbox } from 'primereact/checkbox';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import ModuleAccordionHeader from './ModuleAccordionHeader';
import ReportOption from './ReportOption';
import {
  Access,
  AccessForm,
  EditValues,
  SystemAccess,
} from '../../../Interfaces/admin/Role.interfaces';
import { RegisterFormFields } from './RegisterForm';
import { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { TabMenu } from 'primereact/tabmenu';

type ModulesAccordionProps = {
  modules: Record<string, Record<string, SystemAccess[]>>;
  defaultAccess?: Access[];
  defaultSet?: () => void;
  className?: string;
};

const ModulesAccordion = ({
  modules,
  defaultAccess,
  defaultSet,
  className,
}: ModulesAccordionProps) => {
  const [accordionSections, setAccordionSections] = useState<
    Record<string, JSX.Element[]>
  >({});
  const [selectedModule, setSelectedModule] = useState<string>();
  const [activeSections, setActiveSections] = useState<number[]>([]);
  const [activeModuleIndex, setActiveModuleIndex] = useState(0);
  const [moduleLabelKeys, setModuleLabelKeys] = useState<string[]>([]);
  const [checkedSections, setCheckedSections] = useState<
    Record<string, boolean>
  >({});
  const [checkedModules, setCheckedModules] = useState<Record<string, boolean>>(
    {}
  );
  const { setValue } = useFormContext<RegisterFormFields>();
  const { fields, append } = useFieldArray({
    name: 'access',
  });
  const fieldsInitialized = useRef(fields?.length > 0);
  const currentDefaults = useRef<Access[]>([]);
  const results = useWatch({ name: 'access' });

  const formatEditionValues = (
    edition?: (boolean | 'approval' | 'no-approval')[]
  ) => {
    return edition?.map((option) => {
      let label = `${option}`;
      let value: EditValues = option;

      switch (label) {
        case 'false':
          label = 'View Only';
          value = 'view-only';
          break;
        case 'true':
          label = 'Standard';
          break;
        case 'approval':
          label = 'With Approval';
          break;
        case 'no-approval':
          label = 'Without Approval';
          break;
        default:
          break;
      }

      return { label, value };
    });
  };

  const updateCheckedSections = (section: string, checked: boolean) => {
    setCheckedSections((value) => {
      const tempChecked = { ...value };
      tempChecked[section] = checked;
      return tempChecked;
    });
  };

  const updateCheckedModules = (module: string, checked: boolean) => {
    setCheckedModules((value) => {
      const temp = { ...value };
      temp[module] = checked;
      return temp;
    });
  };

  const moduleTabTemplate = useCallback(
    (item: MenuItem, options: MenuItemOptions) => {
      const label = item.label ?? '';
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          href="#"
          className={options.className}
          onClick={options.onClick}
          role="presentation"
        >
          <span className="p-menuitem-text capitalize">{label}</span>
          <Checkbox
            className="ml-3"
            checked={checkedModules[label] ?? false}
            onChange={(e) => {
              updateCheckedModules(label, e.checked);
              results?.forEach((access: AccessForm, index: number) => {
                if (access.module === label) {
                  setValue(`access.${index}.selected`, e.checked);
                }
              });
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </a>
      );
    },
    [checkedModules, results, setValue]
  );

  const moduleLabels = useMemo(
    () =>
      moduleLabelKeys.map((label) => ({
        label,
        className: 'capitalize',
        template: moduleTabTemplate,
      })),
    [moduleLabelKeys, moduleTabTemplate]
  );

  const accordionTab = (
    section: string,
    startIndex: number,
    children: JSX.Element[]
  ) => {
    return (
      <AccordionTab
        key={section}
        header={
          <ModuleAccordionHeader
            section={section}
            checkedModules={checkedSections}
            updateSection={updateCheckedSections}
            startIndex={startIndex}
            count={children.length}
          />
        }
      >
        <div className="flex flex-column gap-3">{children}</div>
      </AccordionTab>
    );
  };

  const accordionTabCB = useCallback(accordionTab, [checkedSections]);

  const buildAccordion = (fields: AccessForm[]) => {
    const modules: Record<string, JSX.Element[]> = {};
    let result: JSX.Element[] = [];
    let optionsList: JSX.Element[] = [];
    let currentSection = fields[0]?.section;
    let currentModule = fields[0]?.module;

    fields.forEach((item, index) => {
      if (item.section !== currentSection && optionsList.length) {
        result.push(
          accordionTabCB(
            currentSection,
            index - optionsList.length,
            optionsList
          )
        );
        optionsList = [];

        if (item.module !== currentModule) {
          modules[currentModule] = result;
          result = [];
        }
      }

      currentSection = item.section;
      currentModule = item.module;

      const option = (
        <ReportOption key={item.report} item={item} index={index} />
      );
      optionsList.push(option);
    });

    if (optionsList.length) {
      result.push(
        accordionTabCB(
          currentSection,
          fields.length - optionsList.length,
          optionsList
        )
      );
      modules[currentModule] = result;
      result = [];
    }

    return modules;
  };

  const buildAccordionCB = useCallback(buildAccordion, [accordionTabCB]);

  useEffect(() => {
    if (!fieldsInitialized.current) {
      const reports: AccessForm[] = [];
      const keys: string[] = [];

      Object.keys(modules).forEach((moduleKey) => {
        const sections = modules[moduleKey];
        keys.push(moduleKey);

        Object.keys(sections).forEach((sectionKey) => {
          const reportList = sections[sectionKey];

          reportList.forEach((option) => {
            const { value, edition, notification, approval } = option;

            const fields = {
              report: value,
              label: value.replace(/_/g, ' '),
              editOption: formatEditionValues(edition),
              notification,
              section: sectionKey,
              module: moduleKey,
              approvalOption: !!approval,
            };
            reports.push(fields);
          });
        });
      });

      setModuleLabelKeys(keys);
      setSelectedModule(keys[0] ?? undefined);
      append(reports);
      fieldsInitialized.current = true;
    }
  }, [append, modules]);

  useEffect(() => {
    if (fields.length !== 0) {
      const list = buildAccordionCB(fields as unknown as AccessForm[]);
      setAccordionSections(list);
    }
  }, [fields, buildAccordionCB]);

  useEffect(() => {
    if (
      results?.length &&
      defaultAccess &&
      defaultAccess !== currentDefaults.current &&
      (defaultAccess?.length || currentDefaults.current.length)
    ) {
      setActiveSections([]);

      const pevDefaults = currentDefaults.current;

      results.forEach((access: AccessForm, index: number) => {
        const { editable, shouldNotify, shouldApprove } = access;
        const newAccess = { ...access };

        const pevAccess = pevDefaults?.find(
          (pevAccess) => pevAccess.report === access.report
        );

        if (pevAccess) {
          const prevEditable =
            !pevAccess.editable && pevAccess.editable != null
              ? `view-only`
              : pevAccess.editable;

          const sameApproval = shouldApprove == pevAccess.shouldApprove;

          if (
            editable == prevEditable &&
            shouldNotify == pevAccess?.shouldNotify &&
            sameApproval
          ) {
            delete newAccess.selected;
            delete newAccess.editable;
            delete newAccess.shouldNotify;
            delete newAccess.shouldApprove;
          }
        }

        const defAccess = defaultAccess.find(
          (defAccess) => defAccess.report === access.report
        );

        if (defAccess) {
          newAccess.selected = true;

          if (
            newAccess.shouldNotify == null &&
            defAccess.shouldNotify != null
          ) {
            newAccess.shouldNotify = defAccess.shouldNotify;
          }

          if (newAccess.editable == null && defAccess.editable != null) {
            newAccess.editable = !defAccess.editable
              ? `view-only`
              : defAccess.editable;
          }

          if (
            newAccess.shouldApprove == null &&
            defAccess.shouldApprove != null
          ) {
            newAccess.shouldApprove = defAccess.shouldApprove;
          }
        }

        setValue(`access.${index}`, newAccess);
      });

      if (defaultSet) {
        defaultSet();
      }
      currentDefaults.current = defaultAccess;
    }
  }, [results, defaultAccess, setValue, defaultSet]);

  useEffect(() => {
    let currentModule = results ? results[0]?.section : null;
    let counter = 0;
    let checkedCounter = 0;
    results?.forEach((access: AccessForm) => {
      if (access.section === currentModule) {
        counter += 1;
        checkedCounter += access.selected ? 1 : 0;
      } else {
        updateCheckedSections(
          currentModule,
          counter === checkedCounter && counter !== 0
        );

        currentModule = access.section;
        counter = 1;
        checkedCounter = access.selected ? 1 : 0;
      }
    });

    if (counter !== 0) {
      updateCheckedSections(
        currentModule,
        counter === checkedCounter && counter !== 0
      );
    }
  }, [results]);

  useEffect(() => {
    const counts: Record<string, { total: number; checked: number }> = {};
    results?.forEach((access: AccessForm) => {
      if (!counts[access.module])
        counts[access.module] = { total: 0, checked: 0 };
      counts[access.module].total += 1;
      if (access.selected) counts[access.module].checked += 1;
    });
    Object.keys(counts).forEach((mod) => {
      const { total, checked } = counts[mod];
      updateCheckedModules(mod, total > 0 && total === checked);
    });
  }, [results]);

  return (
    <div
      className={`w-full bg-white p-2 border-200 border-1 border-round-md ${className}`}
    >
      <TabMenu
        model={moduleLabels}
        activeIndex={activeModuleIndex}
        onTabChange={(e) => {
          setActiveModuleIndex(e.index);
          setActiveSections([]);
          setSelectedModule(e.value.label);
        }}
        className="w-full"
      />
      <Accordion
        className="w-full mt-2"
        multiple
        activeIndex={activeSections}
        onTabChange={(e) => {
          const element = e.originalEvent.target as HTMLElement;

          if (!element.className.includes('p-checkbox')) {
            setActiveSections(e.index as unknown as number[]);
          }
        }}
      >
        {accordionSections[selectedModule ?? '']}
      </Accordion>
    </div>
  );
};

export default ModulesAccordion;
